import PublicLayout from "@/components/PublicLayout";
import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Users, Activity, Feather, HeartHandshake, MessageSquare, Sparkles, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);

  const load = async () => {
    try {
      const [s, u, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/prompt"),
      ]);
      setStats(s.data);
      setUsers(u.data.items);
      setPrompt(p.data.system_prompt);
    } catch (_e) {
      // ignore
    }
  };

  useEffect(() => {
    load();
  }, []);

  const removeUser = async (id) => {
    if (!window.confirm("Delete this user and all their records?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User removed.");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
  };

  const savePrompt = async () => {
    setSavingPrompt(true);
    try {
      await api.put("/admin/prompt", { system_prompt: prompt });
      toast.success("AI prompt updated.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setSavingPrompt(false);
  };

  return (
    <PublicLayout>
      <section className="bg-brand-base">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
          <p className="text-xs uppercase tracking-widest text-brand-quiet">Admin</p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight">
            Shepherd the community.
          </h1>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatTile icon={Users} label="Total users" value={stats?.total_users ?? "—"} testid="admin-stat-users" />
            <StatTile icon={Activity} label="Active (7d)" value={stats?.active_users ?? "—"} testid="admin-stat-active" />
            <StatTile icon={Feather} label="Sin entries" value={stats?.total_sins ?? "—"} testid="admin-stat-sins" />
            <StatTile icon={HeartHandshake} label="Prayers" value={stats?.total_prayers ?? "—"} testid="admin-stat-prayers" />
            <StatTile icon={MessageSquare} label="Chats" value={stats?.total_chats ?? "—"} testid="admin-stat-chats" />
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 rounded-2xl bg-white border border-brand-line p-6">
              <h3 className="font-serif text-2xl text-brand-ink mb-4">Users</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-brand-quiet border-b border-brand-line">
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Role</th>
                      <th className="py-2 pr-4">Joined</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-brand-line/60" data-testid={`admin-user-${u.id}`}>
                        <td className="py-2.5 pr-4 text-brand-ink">{u.name}</td>
                        <td className="py-2.5 pr-4 text-brand-quiet">{u.email}</td>
                        <td className="py-2.5 pr-4">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              u.role === "admin"
                                ? "bg-brand-mist text-brand-skyDark"
                                : "bg-brand-base border border-brand-line text-brand-quiet"
                            }`}
                          >
                            {u.role}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-brand-muted">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-2.5 text-right">
                          {u.role !== "admin" && (
                            <button
                              onClick={() => removeUser(u.id)}
                              data-testid={`admin-user-delete-${u.id}`}
                              className="text-brand-muted hover:text-brand-setback p-1.5 rounded-full hover:bg-brand-mist"
                              aria-label="Delete user"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-brand-line p-6">
              <h3 className="font-serif text-2xl text-brand-ink mb-3">Top categories</h3>
              {(!stats?.top_categories || stats.top_categories.length === 0) ? (
                <p className="text-sm text-brand-quiet">No data yet.</p>
              ) : (
                <ul className="space-y-3">
                  {stats.top_categories.map((c) => (
                    <li key={c.name} className="flex items-center gap-3">
                      <span className="text-sm text-brand-ink min-w-[7rem]">{c.name}</span>
                      <div className="flex-1 h-2 rounded-full bg-brand-mist overflow-hidden">
                        <div
                          className="h-full bg-brand-sky"
                          style={{
                            width: `${Math.min(
                              100,
                              (c.count / Math.max(...stats.top_categories.map((x) => x.count))) * 100
                            )}%`,
                          }}
                        />
                      </div>
                      <span className="text-sm text-brand-quiet w-8 text-right">{c.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-6 rounded-2xl bg-white border border-brand-line p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-brand-skyDark" strokeWidth={1.6} />
              <h3 className="font-serif text-2xl text-brand-ink">Grace · AI system prompt</h3>
            </div>
            <p className="text-sm text-brand-quiet mb-4">
              Shape the voice of the AI companion. Changes apply to new conversations immediately.
            </p>
            <textarea
              rows={8}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="admin-prompt-textarea"
              className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-3 py-2.5 text-brand-ink text-sm leading-relaxed"
            />
            <div className="flex justify-end mt-3">
              <button
                onClick={savePrompt}
                disabled={savingPrompt}
                data-testid="admin-prompt-save-button"
                className="rounded-full bg-brand-sky text-white hover:bg-brand-skyDark px-5 py-2.5 font-medium inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Save className="w-4 h-4" strokeWidth={1.7} /> {savingPrompt ? "Saving…" : "Save prompt"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function StatTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-brand-quiet">{label}</span>
        <Icon className="w-4 h-4 text-brand-skyDark" strokeWidth={1.6} />
      </div>
      <div className="mt-3 font-serif text-3xl text-brand-ink leading-none">{value}</div>
    </div>
  );
}
